import React, { useState } from "react";
import { FaCameraRetro } from "react-icons/fa";
import { FiSidebar, FiSend } from "react-icons/fi"
import { IoSearch } from "react-icons/io5";
import { GrAttachment } from "react-icons/gr";
import { motion } from "framer-motion";
import { BsLayoutSidebarInset } from "react-icons/bs";

const AgentChat = () => {
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [selectedAgent, setSelectedAgent] = useState("Google Drive Agent");
    const [chatHistory, setChatHistory] = useState([
        { sender: "agent", text: "Hello! How can I assist you today?" },
    ]);
    const [message, setMessage] = useState("");

    const agents = [
        { name: "Google Drive Agent", image: "/assets/google-drive.png" },
        { name: "Database Agent", image: "/assets/database.webp" },
        { name: "Email Agent", image: "/assets/email.png" },
        { name: "Workflow Agent", image: "/assets/workflow.webp" },
    ];

    const sendMessage = () => {
        if (message.trim() === "") return;
        setChatHistory([...chatHistory, { sender: "user", text: message }]);
        setMessage("");
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const sidebarVariants = {
        open: { width: "250px", transition: { duration: 0.3 } },
        closed: { width: "100px", transition: { duration: 0.3 } },
    };

    return (
        <div
            className="flex h-[calc(100vh-140px)] text-white"
            style={{
                background: `
      radial-gradient(circle at 20% 30%, #1a1a1a, transparent 50%), 
      radial-gradient(circle at 80% 70%, #2b2b2b, transparent 50%), 
      radial-gradient(circle at 50% 50%, #0d0d0d, transparent 50%)`,
            }}
        >
            {/* Sidebar */}
            <motion.div
                className="h-full bg-gradient-to-b rounded-r-2xl from-[#1a1a1a] to-[#0a0a0a] border-r border-white/10 shadow-xl relative z-20"
                animate={sidebarOpen ? "open" : "closed"}
                variants={sidebarVariants}
                initial="open"
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-white/10 backdrop-blur-sm bg-black/20">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                        className={`flex items-center space-x-3 ${sidebarOpen ? "block" : "hidden"}`}
                    >
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-white/90 to-white/40 flex items-center justify-center shadow-lg">
                            <span className="text-lg font-bold text-black/80">A</span>
                        </div>
                        <h1 className="text-xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                            Agents
                        </h1>
                    </motion.div>
                    <motion.button
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                    >
                        <BsLayoutSidebarInset className="w-5 h-5 text-gray-400" />
                    </motion.button>
                </div>

                {/* Search Bar */}
                {sidebarOpen && (
                    <motion.div
                        className="p-4"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                    >
                        <div className="flex items-center bg-white/5 rounded-xl p-3 backdrop-blur-sm border border-white/5 hover:border-white/10 transition-colors">
                            <IoSearch className="text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Search agents..."
                                className="bg-transparent text-sm text-white ml-3 focus:outline-none w-full placeholder:text-gray-500"
                            />
                        </div>
                    </motion.div>
                )}

                {/* Agents List */}
                <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
                    <div className="p-3 space-y-2">
                        {agents.map((agent, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className={`flex items-center p-3 rounded-xl cursor-pointer transition-all duration-200
                        ${selectedAgent === agent.name
                                        ? "bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-white/10"
                                        : "hover:bg-white/5"}`}
                                onClick={() => setSelectedAgent(agent.name)}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <div className="relative">
                                    <img
                                        src={agent.image}
                                        alt={agent.name}
                                        className="w-10 h-10 rounded-xl object-cover"
                                    />
                                    <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-[#0a0a0a]" />
                                </div>
                                {sidebarOpen && (
                                    <div className="ml-4 overflow-hidden">
                                        <h2 className="font-semibold text-sm text-white">{agent.name}</h2>
                                        <p className="text-xs text-gray-400 truncate">AI-powered assistant</p>
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* User Profile */}
                {sidebarOpen && (
                    <motion.div
                        className="border-t border-white/10 p-4 backdrop-blur-sm bg-black/20"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <div className="flex items-center space-x-3 p-2 rounded-xl hover:bg-white/5 transition-colors cursor-pointer">
                            <div className="relative">
                                <img
                                    src="https://via.placeholder.com/40"
                                    alt="User"
                                    className="w-10 h-10 rounded-xl object-cover ring-2 ring-white/10"
                                />
                                <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-[#0a0a0a]" />
                            </div>
                            <div className="overflow-hidden">
                                <h3 className="font-medium text-sm text-white">User Name</h3>
                                <p className="text-xs text-gray-400 truncate">username@example.com</p>
                            </div>
                        </div>
                    </motion.div>
                )}
            </motion.div>

            {/* Chat Section */}
            <motion.div
                // className="flex-1 flex flex-col mx-4 my-6 backdrop-blur-md bg-white/10 rounded-2xl shadow-lg"
                className="flex-1 flex flex-col mx-4 my-6 backdrop-blur-md bg-white/5 rounded-2xl shadow-lg"


                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                {/* Chat Header */}
                <div className="p-4 ">
                    <h3 className="text-2xl">{selectedAgent}</h3>
                </div>

                {/* Chat Messages */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {chatHistory.map((chat, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                            className={`flex ${chat.sender === "user" ? "justify-end" : "justify-start"}`}
                        >
                            <div
                                className={`max-w-[75%] p-4 rounded-xl ${chat.sender === "user"
                                    ? "bg-white text-black"
                                    : "bg-black text-white"
                                    }`}
                            >
                                {chat.text}
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Message Input */}
                <div className="p-4">
                    <div className="flex items-center bg-black  rounded-full px-4 py-2">
                        <div className="relative">
                            <motion.button
                                className="p-2 text-gray-400 hover:text-gray-300"
                                whileTap={{ scale: 0.9 }}
                            >
                                <GrAttachment className="w-5 h-5" />
                            </motion.button>
                            <input
                                type="file"
                                accept=".pdf,.doc,.docx,.txt"
                                onChange={(e) => {
                                    if (e.target.files?.[0]) {
                                        const file = e.target.files[0];
                                        console.log("Document uploaded:", file);
                                        alert(`Document uploaded: ${file.name}`);
                                    }
                                }}
                                className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
                            />
                        </div>

                        <div className="relative">
                            <motion.button
                                className="p-2 text-gray-400 hover:text-gray-300"
                                whileTap={{ scale: 0.9 }}
                            >
                                <FaCameraRetro className="w-5 h-5" />
                            </motion.button>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                    if (e.target.files?.[0]) {
                                        const file = e.target.files[0];
                                        console.log("Image uploaded:", file);
                                        alert(`Image uploaded: ${file.name}`);
                                    }
                                }}
                                className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
                            />
                        </div>
                        <textarea
                            className="flex-1 bg-transparent text-white px-4 py-2 focus:outline-none placeholder-gray-400 resize-none"
                            placeholder={`Ask ${selectedAgent} a question...`}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            onKeyDown={handleKeyPress}
                        />
                        <button
                            onClick={sendMessage}
                            className={`p-2 ml-3 rounded-full ${message.trim()
                                ? "bg-white text-black hover:bg-blue-500"
                                : "bg-white text-black"
                                }`}
                        >
                            <FiSend className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default AgentChat;











































// import React, { useState } from "react";
// import {
//   FaBars,
//   FaSearch,
//   FaPaperPlane,
//   FaPaperclip,
// } from "react-icons/fa";
// import { motion } from "framer-motion";

// const AgentChat = () => {
//   const [sidebarOpen, setSidebarOpen] = useState(true);
//   const [selectedAgent, setSelectedAgent] = useState("Google Drive Agent");
//   const [chatHistory, setChatHistory] = useState([
//     { sender: "agent", text: "Hello! How can I assist you today?" },
//   ]);
//   const [message, setMessage] = useState("");

//   const agents = [
//     { name: "Google Drive Agent", image: "/assets/google-drive.png" },
//     { name: "Database Agent", image: "/assets/database.webp" },
//     { name: "Email Agent", image: "/assets/email.png" },
//     { name: "Workflow Agent", image: "/assets/workflow.webp" },
//   ];

//   const sendMessage = () => {
//     if (message.trim() === "") return;
//     setChatHistory([...chatHistory, { sender: "user", text: message }]);
//     setMessage("");
//   };

//   const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
//     if (e.key === "Enter" && !e.shiftKey) {
//       e.preventDefault();
//       sendMessage();
//     }
//   };

//   return (
//     <div className="flex h-screen bg-gray-900 text-white">
//       {/* Floating Sidebar */}
//       <motion.div
//         className="absolute left-4 top-4 bg-black text-white w-[300px] h-[85%] p-6 rounded-2xl shadow-xl z-10"
//         initial={{ opacity: 0, x: -50 }}
//         animate={{ opacity: 1, x: 0 }}
//         transition={{ duration: 0.5 }}
//       >
//         {/* Sidebar Header */}
//         <div className="flex items-center justify-between mb-6">
//           <h1 className="text-lg font-bold">Agents</h1>
//           <button
//             className="text-gray-400 hover:text-white"
//             onClick={() => setSidebarOpen(!sidebarOpen)}
//           >
//             <FaBars className="w-6 h-6" />
//           </button>
//         </div>

//         {/* Agent List */}
//         <div className="space-y-4 overflow-y-auto h-full">
//           {agents.map((agent, index) => (
//             <div
//               key={index}
//               className={`flex items-center p-3 rounded-lg cursor-pointer hover:bg-gray-800 ${
//                 selectedAgent === agent.name ? "bg-gray-800" : ""
//               }`}
//               onClick={() => setSelectedAgent(agent.name)}
//             >
//               {/* Agent Image */}
//               <img
//                 src={agent.image}
//                 alt={agent.name}
//                 className="w-12 h-12 rounded-full object-cover"
//               />
//               <div className="ml-4">
//                 <h2 className="font-bold">{agent.name}</h2>
//                 <p className="text-sm text-gray-400">AI-powered assistant</p>
//               </div>
//             </div>
//           ))}
//         </div>
//       </motion.div>

//       {/* Floating Chat Window */}
//       <motion.div
//         className="absolute right-4 bottom-4 bg-black text-white w-[80%] h-[90%] p-8 rounded-2xl shadow-2xl flex flex-col"
//         initial={{ opacity: 0, y: 50 }}
//         animate={{ opacity: 1, y: 0 }}
//         transition={{ duration: 0.5 }}
//       >
//         {/* Chat Header */}
//         <div className="flex justify-between items-center mb-4">
//           <h2 className="text-2xl font-bold">{selectedAgent}</h2>
//         </div>

//         {/* Chat Messages */}
//         <div className="flex-1 overflow-y-auto space-y-4 bg-gray-800 p-6 rounded-lg">
//           {chatHistory.map((chat, index) => (
//             <motion.div
//               key={index}
//               initial={{ opacity: 0, y: 10 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.3 }}
//               className={`flex ${
//                 chat.sender === "user" ? "justify-end" : "justify-start"
//               }`}
//             >
//               <div
//                 className={`max-w-[75%] p-4 rounded-xl ${
//                   chat.sender === "user"
//                     ? "bg-blue-600 text-white"
//                     : "bg-gray-700 text-white"
//                 }`}
//               >
//                 {chat.text}
//               </div>
//             </motion.div>
//           ))}
//         </div>

//         {/* Message Input */}
//         <div className="flex items-center justify-between mt-6">
//           <button className="p-3 text-gray-400 hover:text-gray-300">
//             <FaPaperclip className="w-5 h-5" />
//           </button>
//           <textarea
//             className="flex-1 bg-gray-800 text-white px-6 py-3 rounded-full focus:outline-none placeholder-gray-400 resize-none text-lg"
//             placeholder={`Type a message to ${selectedAgent}...`}
//             value={message}
//             onChange={(e) => setMessage(e.target.value)}
//             onKeyDown={handleKeyPress}
//             rows={1}
//           />
//           <button
//             onClick={sendMessage}
//             className={`p-3 ml-3 flex items-center justify-center rounded-full ${
//               message.trim()
//                 ? "bg-blue-600 text-white hover:bg-blue-500"
//                 : "bg-gray-700 text-gray-400"
//             }`}
//           >
//             <FaPaperPlane className="w-6 h-6" />
//           </button>
//         </div>
//       </motion.div>
//     </div>
//   );
// };

// export default AgentChat;


// import React, { useState } from "react";
// import {
//   FaBars,
//   FaSearch,
//   FaPaperPlane,
//   FaPlus,
//   FaCloud,
//   FaDatabase,
//   FaEnvelope,
//   FaRobot,
//   FaPaperclip,
//   FaArrowLeft,
// } from "react-icons/fa";
// import { motion } from "framer-motion";
// import { useNavigate } from "react-router-dom"; // Use this for navigation

// const AgentChat = () => {
//   const [sidebarOpen, setSidebarOpen] = useState(true);
//   const [selectedAgent, setSelectedAgent] = useState("Google Drive Agent");
//   const [chatHistory, setChatHistory] = useState([
//     { sender: "agent", text: "Hello! How can I assist you today?" },
//   ]);
//   const [message, setMessage] = useState("");

//   const navigate = useNavigate(); // For navigation

//   const agents = [
//     {
//       name: "Google Drive Agent",
//       image: "/assets/google-drive.png",
//     },
//     {
//       name: "Database Agent",
//       image: "/assets/database.webp",
//     },
//     {
//       name: "Email Agent",
//       image: "/assets/email.png",
//     },
//     {
//       name: "Workflow Agent",
//       image: "/assets/workflow.webp",
//     },
//   ];

//   const sendMessage = () => {
//     if (message.trim() === "") return;
//     setChatHistory([...chatHistory, { sender: "user", text: message }]);
//     setMessage("");
//   };

//   const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
//     if (e.key === "Enter" && !e.shiftKey) {
//       e.preventDefault(); // Prevent new line
//       sendMessage();
//     }
//   };

//   const sidebarVariants = {
//     open: { width: "250px", transition: { duration: 0.3 } },
//     closed: { width: "70px", transition: { duration: 0.3 } },
//   };

//   return (
//     <div className="flex h-screen bg-black text-white">
//       {/* Sidebar */}
//       <motion.aside
//         className="bg-black flex flex-col"
//         animate={sidebarOpen ? "open" : "closed"}
//         variants={sidebarVariants}
//         initial="open"
//       >
//         {/* Sidebar Header */}
//         <div className="flex items-center justify-between p-4 border-b border-gray-700">
//           <h1
//             className={`text-lg font-bold ${
//               sidebarOpen ? "block" : "hidden"
//             } overflow-hidden`}
//           >
//             Agents
//           </h1>
//           <button
//             className="text-gray-400 hover:text-white"
//             onClick={() => setSidebarOpen(!sidebarOpen)}
//           >
//             <FaBars className="w-6 h-6" />
//           </button>
//         </div>

//         {/* Search Bar */}
//         <div
//           className={`p-4 ${sidebarOpen ? "block" : "hidden"} transition-all`}
//         >
//           <div className="flex items-center bg-black rounded-lg p-2">
//             <FaSearch className="text-gray-400 w-5 h-5" />
//             <input
//               type="text"
//               placeholder="Search agents"
//               className="bg-transparent text-white ml-2 focus:outline-none w-full"
//             />
//           </div>
//         </div>

//         {/* Agent List */}
//         <div className="flex-1 overflow-y-auto">
//           {agents.map((agent, index) => (
//             <div
//               key={index}
//               className={`flex items-center p-4 hover:bg-gray-800 cursor-pointer ${
//                 selectedAgent === agent.name ? "bg-gray-800" : ""
//               }`}
//               onClick={() => setSelectedAgent(agent.name)}
//             >
//               <img
//                 src={agent.image}
//                 alt={agent.name}
//                 className="w-10 h-10 rounded-full object-cover"
//               />
//               <div
//                 className={`ml-3 ${sidebarOpen ? "block" : "hidden"} transition-all`}
//               >
//                 <h2 className="font-bold">{agent.name}</h2>
//                 <p className="text-sm text-gray-400">AI-powered assistant</p>
//               </div>
//             </div>
//           ))}
//         </div>

//         {/* Sidebar Footer */}
//         <div className="p-4 flex items-center">
//           <img
//             src="https://via.placeholder.com/40"
//             alt="User"
//             className="w-10 h-10 rounded-full"
//           />
//           <div className={`ml-3 ${sidebarOpen ? "block" : "hidden"}`}>
//             <h3 className="font-bold">User Name</h3>
//             <p className="text-sm text-gray-400">username@example.com</p>
//           </div>
//         </div>
//       </motion.aside>

//       {/* Chat Section */}
//       <div className="flex-1 flex flex-col">
//         {/* Breadcrumbs */}
//         <div className="flex items-center px-4 py-2 bg-black">
//           <button
//             className="text-white flex items-center space-x-2"
//             onClick={() => navigate(-1)} // Navigate to the previous page
//           >
//             <FaArrowLeft className="w-5 h-5" />
//             {/* <span className="text-sm">Back</span> */}
//           </button>
//         </div>

//         {/* Chat Messages */}
//         <div className="flex-1 overflow-y-auto p-4 space-y-4">
//           {chatHistory.map((chat, index) => (
//             <motion.div
//               key={index}
//               initial={{ opacity: 0, y: 10 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.3 }}
//               className={`flex ${
//                 chat.sender === "user" ? "justify-end" : "justify-start"
//               }`}
//             >
//               <div
//                 className={`max-w-[70%] p-4 rounded-xl ${
//                   chat.sender === "user"
//                     ? "bg-white text-black"
//                     : "bg-black text-white"
//                 }`}
//               >
//                 {chat.text}
//               </div>
//             </motion.div>
//           ))}
//         </div>

//         {/* Message Input */}
//         <div className="p-4 bg-black flex justify-center">
//   <div className="w-full max-w-3xl flex items-center bg-black border border-gray-700 rounded-lg px-4 py-2">
//     <button className="p-2 text-gray-400 hover:text-gray-300">
//       <FaPaperclip className="w-5 h-5" />
//     </button>
//     <textarea
//       className="flex-1 bg-transparent text-white px-4 py-2 focus:outline-none placeholder-gray-400 resize-none"
//       placeholder={`Ask ${selectedAgent} a question...`}
//       value={message}
//       onChange={(e) => setMessage(e.target.value)}
//       onKeyDown={handleKeyPress}
//       rows={1} // Controls the initial height
//     />
//     <button
//       onClick={sendMessage}
//       className={`p-2 flex items-center justify-center rounded-lg ${
//         message.trim()
//           ? "bg-blue-600 text-white hover:bg-blue-500"
//           : "bg-gray-700 text-gray-400"
//       }`}
//     >
//       <FaPaperPlane className="w-5 h-5" />
//     </button>
//   </div>
// </div>
//       </div>
//     </div>
//   );
// };

// export default AgentChat;


// import React, { useState } from "react";
// import {
//     FaBars,
//     FaSearch,
//     FaPaperPlane,
//     FaPlus,
//     FaCloud,
//     FaDatabase,
//     FaEnvelope,
//     FaRobot,
//     FaPaperclip
// } from "react-icons/fa";
// import { motion } from "framer-motion";

// const AgentChat = () => {
//     const [sidebarOpen, setSidebarOpen] = useState(true);
//     const [selectedAgent, setSelectedAgent] = useState("Google Drive Agent");
//     const [chatHistory, setChatHistory] = useState([
//         { sender: "agent", text: "Hello! How can I assist you today?" },
//     ]);
//     const [message, setMessage] = useState("");

//     //   const agents = [
//     //     {
//     //       name: "Google Drive Agent",
//     //       icon: <FaCloud className="text-blue-500 w-6 h-6" />,
//     //     },
//     //     {
//     //       name: "Database Agent",
//     //       icon: <FaDatabase className="text-green-500 w-6 h-6" />,
//     //     },
//     //     {
//     //       name: "Email Agent",
//     //       icon: <FaEnvelope className="text-red-500 w-6 h-6" />,
//     //     },
//     //     {
//     //       name: "Workflow Agent",
//     //       icon: <FaRobot className="text-purple-500 w-6 h-6" />,
//     //     },
//     //   ];

//     const agents = [
//         {
//             name: "Google Drive Agent",
//             image: "/assets/google-drive.png", // Path to the image
//         },
//         {
//             name: "Database Agent",
//             image: "/assets/database.webp", // Path to the image
//         },
//         {
//             name: "Email Agent",
//             image: "/assets/email.png", // Path to the image
//         },
//         {
//             name: "Workflow Agent",
//             image: "/assets/workflow.webp", // Path to the image
//         },
//     ];

//     const sendMessage = () => {
//         if (message.trim() === "") return;
//         setChatHistory([...chatHistory, { sender: "user", text: message }]);
//         setMessage("");
//     };

//     const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
//         if (e.key === "Enter" && !e.shiftKey) {
//             e.preventDefault(); // Prevent new line
//             sendMessage();
//         }
//     };

//     const sidebarVariants = {
//         open: { width: "250px", transition: { duration: 0.3 } },
//         closed: { width: "70px", transition: { duration: 0.3 } },
//     };

//     return (
//         <div className="flex h-screen bg-black text-white">
//             {/* Sidebar */}
//             <motion.aside
//                 className="bg-black flex flex-col"
//                 animate={sidebarOpen ? "open" : "closed"}
//                 variants={sidebarVariants}
//                 initial="open"
//             >
//                 {/* Sidebar Header */}
//                 <div className="flex items-center justify-between p-4 border-b border-gray-700">
//                     <h1
//                         className={`text-lg font-bold ${sidebarOpen ? "block" : "hidden"
//                             } overflow-hidden`}
//                     >
//                         Agents
//                     </h1>
//                     <button
//                         className="text-gray-400 hover:text-white"
//                         onClick={() => setSidebarOpen(!sidebarOpen)}
//                     >
//                         <FaBars className="w-6 h-6" />
//                     </button>
//                 </div>

//                 {/* Search Bar */}
//                 <div
//                     className={`p-4 ${sidebarOpen ? "block" : "hidden"} transition-all`}
//                 >
//                     <div className="flex items-center bg-black rounded-lg p-2">
//                         <FaSearch className="text-gray-400 w-5 h-5" />
//                         <input
//                             type="text"
//                             placeholder="Search agents"
//                             className="bg-transparent text-white ml-2 focus:outline-none w-full"
//                         />
//                     </div>
//                 </div>

//                 {/* Agent List */}
//                 {/* <div className="flex-1 overflow-y-auto">
//           {agents.map((agent, index) => (
//             <div
//               key={index}
//               className={`flex items-center p-4 hover:bg-gray-800 cursor-pointer ${
//                 selectedAgent === agent.name ? "bg-gray-800" : ""
//               }`}
//               onClick={() => setSelectedAgent(agent.name)}
//             >
//               {agent.icon}
//               <div
//                 className={`ml-3 ${
//                   sidebarOpen ? "block" : "hidden"
//                 } transition-all`}
//               >
//                 <h2 className="font-bold">{agent.name}</h2>
//                 <p className="text-sm text-gray-400">AI-powered assistant</p>
//               </div>
//             </div>
//           ))}
//         </div> */}
//                 <div className="flex-1 overflow-y-auto">
//                     {agents.map((agent, index) => (
//                         <div
//                             key={index}
//                             className={`flex items-center p-4 hover:bg-gray-800 cursor-pointer ${selectedAgent === agent.name ? "bg-gray-800" : ""
//                                 }`}
//                             onClick={() => setSelectedAgent(agent.name)}
//                         >
//                             {/* Agent Image */}
//                             <img
//                                 src={agent.image}
//                                 alt={agent.name}
//                                 className="w-10 h-10 rounded-full object-cover"
//                             />
//                             <div
//                                 className={`ml-3 ${sidebarOpen ? "block" : "hidden"} transition-all`}
//                             >
//                                 <h2 className="font-bold">{agent.name}</h2>
//                                 <p className="text-sm text-gray-400">AI-powered assistant</p>
//                             </div>
//                         </div>
//                     ))}
//                 </div>

//                 {/* Sidebar Footer */}
//                 <div className="p-4 flex items-center">
//                     <img
//                         src="https://via.placeholder.com/40"
//                         alt="User"
//                         className="w-10 h-10 rounded-full"
//                     />
//                     <div className={`ml-3 ${sidebarOpen ? "block" : "hidden"}`}>
//                         <h3 className="font-bold">User Name</h3>
//                         <p className="text-sm text-gray-400">username@example.com</p>
//                     </div>
//                 </div>
//             </motion.aside>

//             {/* Chat Section */}
//             <div className="flex-1 flex flex-col">
//                 {/* Chat Header */}
//                 <div className="p-4  flex justify-between items-center">
//                     <h2 className="text-lg font-bold">{selectedAgent}</h2>
//                 </div>

//                 {/* Chat Messages */}
//                 <div className="flex-1 overflow-y-auto p-4 space-y-4">
//                     {chatHistory.map((chat, index) => (
//                         <motion.div
//                             key={index}
//                             initial={{ opacity: 0, y: 10 }}
//                             animate={{ opacity: 1, y: 0 }}
//                             transition={{ duration: 0.3 }}
//                             className={`flex ${chat.sender === "user" ? "justify-end" : "justify-start"
//                                 }`}
//                         >
//                             <div
//                                 className={`max-w-[70%] p-4 rounded-xl ${chat.sender === "user"
//                                         ? "bg-white text-black"
//                                         : "bg-black text-white"
//                                     }`}
//                             >
//                                 {chat.text}
//                             </div>
//                         </motion.div>
//                     ))}
//                 </div>

//                 {/* Message Input */}
//                 <div className="relative flex items-center justify-center w-full">
//                     <div className="absolute bottom-16 w-3/4 md:w-1/2 bg-black flex items-center border border-gray-700 rounded-lg px-4 py-2 shadow-md">
//                         <button className="p-2 bg-gray-800 text-gray-400 rounded-lg hover:bg-gray-700">
//                             <FaPaperclip className="w-5 h-5" />
//                         </button>
//                         <input
//                             className="flex-1 bg-black text-gray-300 px-4 py-2 focus:outline-none text-sm"
//                             placeholder={`Ask ${selectedAgent} a question...`}
//                             value={message}
//                             onChange={(e) => setMessage(e.target.value)}
//                             onKeyDown={handleKeyPress}
//                         />
//                         <button
//                             onClick={sendMessage}
//                             className={`ml-3 p-2 flex items-center justify-center rounded-lg ${message.trim()
//                                     ? "bg-blue-600 text-white hover:bg-blue-500"
//                                     : "bg-gray-800 text-gray-400"
//                                 }`}
//                         >
//                             <FaPaperPlane className="w-5 h-5" />
//                         </button>
//                     </div>
//                 </div>
//             </div>
//         </div>
//     );
// };

// export default AgentChat;


// import React, { useState } from "react";
// import { motion } from "framer-motion";
// import { 
//   FaTimes as X, 
//   FaPaperclip as Paperclip, 
//   FaPaperPlane as Send, 
//   FaRobot as Workflow, 
//   FaCloud as Drive, 
//   FaDatabase as Database, 
//   FaEnvelope as Email 
// } from "react-icons/fa";

// const AgentChat = () => {
//   const [selectedAgent, setSelectedAgent] = useState("Google Drive Agent");
//   const [chatHistory, setChatHistory] = useState<string[]>([
//     "Hello! How can I assist you today?",
//   ]);
//   const [newMessage, setNewMessage] = useState("");
//   const [isTyping, setIsTyping] = useState(false);

//   const agents = [
//     { name: "Google Drive Agent", icon: <Drive className="w-6 h-6 text-blue-600" /> },
//     { name: "Database Agent", icon: <Database className="w-6 h-6 text-green-600" /> },
//     { name: "Email Agent", icon: <Email className="w-6 h-6 text-red-600" /> },
//     { name: "Workflow Agent", icon: <Workflow className="w-6 h-6 text-purple-600" /> },
//   ];

//   const handleSendMessage = () => {
//     if (!newMessage.trim()) return;

//     setChatHistory((prev) => [...prev, `You: ${newMessage}`]);
//     setNewMessage("");
//     setIsTyping(true);

//     // Simulating an agent response
//     setTimeout(() => {
//       setChatHistory((prev) => [
//         ...prev,
//         `${selectedAgent}: Sure, let me assist you with that!`,
//       ]);
//       setIsTyping(false);
//     }, 1500);
//   };

//   const messageVariants = {
//     hidden: { opacity: 0, y: 20 },
//     visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
//   };

//   return (
//     <div className="flex h-screen bg-[#F8F9FB] flex-col md:flex-row">
//       {/* Sidebar */}
//       <aside className="w-full md:w-[68px] bg-white border-r border-gray-200 flex flex-row md:flex-col items-center py-4 overflow-x-auto md:overflow-visible">
//         <div className="flex flex-row md:flex-col items-center justify-between space-x-4 md:space-y-6 px-4 md:px-0">
//           <motion.button whileHover={{ scale: 1.1 }} className="p-2.5 rounded-lg hover:bg-gray-100">
//             <X className="w-6 h-6 text-gray-600" />
//           </motion.button>
//         </div>
//       </aside>

//       {/* Main Chat Section */}
//       <div className="flex-1 px-8 py-12 flex flex-col py-32">
//         {/* Header */}
//         <motion.div
//           className="flex justify-between items-center mb-6"
//           initial={{ opacity: 0, y: -20 }}
//           animate={{ opacity: 1, y: 0, transition: { duration: 0.5 } }}
//         >
//           <h1
//             className="text-[2.5rem] font-bold"
//             style={{
//               background: "linear-gradient(90deg, #000000, #ff006e, #9333EA)",
//               WebkitBackgroundClip: "text",
//               WebkitTextFillColor: "transparent",
//             }}
//           >
//             Chat with {selectedAgent}
//           </h1>
//           <select
//             value={selectedAgent}
//             onChange={(e) => setSelectedAgent(e.target.value)}
//             className="bg-white text-gray-600 px-4 py-2 rounded-lg border border-gray-200 focus:outline-none"
//           >
//             {agents.map((agent, index) => (
//               <option key={index} value={agent.name}>
//                 {agent.name}
//               </option>
//             ))}
//           </select>
//         </motion.div>

//         {/* Chat History */}
//         <div className="flex-1 overflow-y-auto mb-4">
//           {chatHistory.map((message, index) => (
//             <motion.div
//               key={index}
//               className={`mb-4 ${
//                 message.startsWith("You:") ? "text-right" : "text-left"
//               }`}
//               variants={messageVariants}
//               initial="hidden"
//               animate="visible"
//             >
//               <div
//                 className={`inline-block px-4 py-3 rounded-lg ${
//                   message.startsWith("You:")
//                     ? "bg-[#4F46E5] text-white"
//                     : "bg-gray-200 text-gray-800"
//                 }`}
//               >
//                 {message}
//               </div>
//             </motion.div>
//           ))}
//           {isTyping && (
//             <motion.div
//               className="mb-4 text-left"
//               variants={messageVariants}
//               initial="hidden"
//               animate="visible"
//             >
//               <div className="inline-block px-4 py-3 rounded-lg bg-gray-200 text-gray-800">
//                 {selectedAgent} is typing...
//               </div>
//             </motion.div>
//           )}
//         </div>

//         {/* Input Area */}
//         <div className="relative bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center">
//           <button className="text-gray-400 hover:text-gray-600 transition-colors mr-4">
//             <Paperclip className="w-5 h-5" />
//           </button>
//           <textarea
//             className="flex-1 resize-none px-4 py-2 focus:outline-none text-gray-700 placeholder-gray-500 text-[15px]"
//             placeholder="Type your message..."
//             value={newMessage}
//             onChange={(e) => setNewMessage(e.target.value)}
//           />
//           <button
//             onClick={handleSendMessage}
//             className={`ml-4 p-3 rounded-full ${
//               newMessage.trim()
//                 ? "bg-[#4F46E5] text-white hover:bg-[#4338CA]"
//                 : "bg-gray-100 text-gray-400"
//             } flex items-center justify-center`}
//           >
//             <Send className="w-5 h-5" />
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AgentChat;


// import React, { useState } from "react";
// import { 
//     FaTimes as X, 
//     FaPlus as Plus, 
//     FaSearch as Search, 
//     FaHome as Home, 
//     FaFileAlt as FileText, 
//     FaClock as Clock, 
//     FaSyncAlt as RefreshCw, 
//     FaPaperclip as Paperclip, 
//     FaPaperPlane as Send, 
//     FaCloud as Drive, 
//     FaDatabase as Database, 
//     FaEnvelope as Email, 
//     FaRobot as Workflow 
//   } from 'react-icons/fa';
// import { motion } from "framer-motion";

// const AgentChat = () => {
//   const [message, setMessage] = useState('');
//   const [selectedAgent, setSelectedAgent] = useState("Google Drive Agent");

//   const agents = [
//     {
//       name: "Google Drive Agent",
//       description: "Manage and interact with your Google Drive files seamlessly.",
//       icon: <Drive className="w-6 h-6 text-blue-600" />,
//     },
//     {
//       name: "Database Agent",
//       description: "Query and manage your database with AI assistance.",
//       icon: <Database className="w-6 h-6 text-green-600" />,
//     },
//     {
//       name: "Email Agent",
//       description: "Compose, organize, and manage your emails effectively.",
//       icon: <Email className="w-6 h-6 text-red-600" />,
//     },
//     {
//       name: "Workflow Agent",
//       description: "Automate and streamline your workflows effortlessly.",
//       icon: <Workflow className="w-6 h-6 text-purple-600" />,
//     },
//   ];

//   const selectAgent = (agent: string) => {
//     setSelectedAgent(agent);
//     setMessage(""); // Reset chat message when changing agents
//   };

//   const containerVariants = {
//     hidden: { opacity: 0, y: 50 },
//     visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
//   };

//   const agentCardVariants = {
//     hidden: { opacity: 0, scale: 0.8 },
//     visible: { opacity: 1, scale: 1, transition: { duration: 0.4 } },
//     hover: { scale: 1.05, boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.2)" },
//   };

//   const messageBoxVariants = {
//     hidden: { opacity: 0, y: 20 },
//     visible: { opacity: 1, y: 0, transition: { delay: 0.2, duration: 0.5 } },
//   };

//   return (
//     <div className="flex h-screen bg-[#F8F9FB] flex-col md:flex-row">
//       {/* Sidebar */}
//       <aside className="w-full md:w-[68px] bg-white border-r border-gray-200 flex flex-row md:flex-col items-center py-4 overflow-x-auto md:overflow-visible">
//         <div className="flex flex-row md:flex-col items-center justify-between space-x-4 md:space-y-6 px-4 md:px-0">
//           <motion.button whileHover={{ scale: 1.1 }} className="p-2.5 rounded-lg hover:bg-gray-100">
//             <X className="w-6 h-6 text-gray-600" />
//           </motion.button>
//           <motion.button whileHover={{ scale: 1.1 }} className="p-2.5 rounded-lg hover:bg-gray-100">
//             <Plus className="w-6 h-6 text-gray-600" />
//           </motion.button>
//           <motion.button whileHover={{ scale: 1.1 }} className="p-2.5 rounded-lg hover:bg-gray-100">
//             <Search className="w-6 h-6 text-gray-600" />
//           </motion.button>
//           <motion.button whileHover={{ scale: 1.1 }} className="p-2.5 rounded-lg hover:bg-gray-100">
//             <Home className="w-6 h-6 text-gray-600" />
//           </motion.button>
//         </div>
//       </aside>

//       {/* Main Content */}
//       <motion.main 
//         className="flex-1 px-8 py-12"
//         variants={containerVariants}
//         initial="hidden"
//         animate="visible"
//       >
//         <div className="max-w-[1200px] mx-auto py-32">
//           {/* Welcome Section */}
//           <motion.div className="mb-10" initial="hidden" animate="visible" variants={containerVariants}>
//             <h1
//               className="text-[2.75rem] leading-tight font-bold mb-1"
//               style={{
//                 background: "linear-gradient(90deg, #000000, #ff006e, #9333EA)",
//                 WebkitBackgroundClip: "text",
//                 WebkitTextFillColor: "transparent",
//               }}
//             >
//               Choose Your Agent
//             </h1>
//             <p className="text-gray-500 text-[0.925rem]">
//               Select an agent below to assist you with specific tasks.
//             </p>
//           </motion.div>

//           {/* Agents Grid */}
//           <motion.div 
//             className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10"
//             initial="hidden"
//             animate="visible"
//             variants={containerVariants}
//           >
//             {agents.map((agent, index) => (
//               <motion.button
//                 key={index}
//                 onClick={() => selectAgent(agent.name)}
//                 className={`bg-white p-5 rounded-xl border-2 ${
//                   selectedAgent === agent.name ? "border-blue-500 shadow-lg" : "border-gray-100"
//                 } hover:shadow-md transition-shadow duration-200`}
//                 variants={agentCardVariants}
//                 whileHover="hover"
//               >
//                 <div className="flex items-center space-x-4">
//                   {agent.icon}
//                   <div>
//                     <h3 className="text-lg font-bold text-gray-800">{agent.name}</h3>
//                     <p className="text-sm text-gray-500">{agent.description}</p>
//                   </div>
//                 </div>
//               </motion.button>
//             ))}
//           </motion.div>

//           {/* Current Agent */}
//           <motion.div 
//             className="bg-white p-6 rounded-2xl shadow-md mb-6"
//             variants={messageBoxVariants}
//           >
//             <h2 className="text-xl font-bold text-gray-800 mb-2">Chatting with {selectedAgent}</h2>
//             <p className="text-gray-500 text-sm">
//               Start a conversation to interact with the {selectedAgent}.
//             </p>
//           </motion.div>

//           {/* Input Area */}
//           <motion.div 
//             className="relative bg-white rounded-2xl border border-gray-100 shadow-sm"
//             variants={messageBoxVariants}
//           >
//             <textarea
//               className="w-full resize-none px-6 pt-5 pb-16 rounded-2xl focus:outline-none text-gray-700 placeholder-gray-500 text-[15px]"
//               placeholder={`Ask ${selectedAgent}...`}
//               value={message}
//               onChange={(e) => setMessage(e.target.value)}
//               rows={1}
//             />
//             <div className="absolute bottom-4 inset-x-6 flex justify-between items-center">
//               <div className="flex items-center space-x-4">
//                 <button className="text-gray-400 hover:text-gray-600 transition-colors">
//                   <Paperclip className="w-5 h-5" />
//                 </button>
//               </div>
//               <button
//                 className={`p-2 rounded-lg flex items-center justify-center ${
//                   message.trim()
//                     ? "bg-[#4F46E5] text-white hover:bg-[#4338CA]"
//                     : "bg-gray-100 text-gray-400"
//                 }`}
//               >
//                 <Send className="w-4 h-4" />
//               </button>
//             </div>
//           </motion.div>
//         </div>
//       </motion.main>
//     </div>
//   );
// };

// export default AgentChat;


// import React, { useState } from "react";
// import { 
//     FaTimes as X, 
//     FaPlus as Plus, 
//     FaSearch as Search, 
//     FaHome as Home, 
//     FaFileAlt as FileText, 
//     FaClock as Clock, 
//     FaSyncAlt as RefreshCw, 
//     FaPaperclip as Paperclip, 
//     FaImage as ImageIcon, 
//     FaPaperPlane as Send, 
//     FaUser as User, 
//     FaEnvelope as Mail, 
//     FaFileAlt as Document, 
//     FaCog as Settings 
//   } from 'react-icons/fa';

// const AgentChat = () => {
//   const [message, setMessage] = useState('');
//   const prompts = [
//     {
//       title: 'Write a to-do list for a personal project or task',
//       icon: <User className="w-5 h-5 text-gray-700" />
//     },
//     {
//       title: 'Generate an email to reply to a job offer',
//       icon: <Mail className="w-5 h-5 text-gray-700" />
//     },
//     {
//       title: 'Summarise this article or text for me in one paragraph',
//       icon: <Document className="w-5 h-5 text-gray-700" />
//     },
//     {
//       title: 'How does AI work in a technical capacity',
//       icon: <Settings className="w-5 h-5 text-gray-700" />
//     }
//   ];

//   return (
//     <div className="flex h-screen bg-[#F8F9FB] pt-16"> {/* Added padding-top */}
//       {/* Sidebar */}
//       <aside className="w-[68px] bg-white border-r border-gray-200 flex flex-col items-center py-4">
//         <div className="flex flex-col items-center space-y-6">
//           <button className="p-2.5 rounded-lg hover:bg-gray-100">
//             <X className="w-6 h-6 text-gray-600" />
//           </button>
//           <button className="p-2.5 rounded-lg hover:bg-gray-100">
//             <Plus className="w-6 h-6 text-gray-600" />
//           </button>
//           <button className="p-2.5 rounded-lg hover:bg-gray-100">
//             <Search className="w-6 h-6 text-gray-600" />
//           </button>
//           <button className="p-2.5 rounded-lg hover:bg-gray-100">
//             <Home className="w-6 h-6 text-gray-600" />
//           </button>
//           <button className="p-2.5 rounded-lg hover:bg-gray-100">
//             <FileText className="w-6 h-6 text-gray-600" />
//           </button>
//           <button className="p-2.5 rounded-lg hover:bg-gray-100">
//             <Clock className="w-6 h-6 text-gray-600" />
//           </button>
//         </div>
//       </aside>

//       {/* Main Content */}
//       <main className="flex-1 px-8 py-12">
//         <div className="max-w-[1200px] mx-auto">
//           {/* Welcome Message */}
//           {/* <div className="mb-10">
//             <h1
//               className="text-[2.75rem] leading-tight font-bold mb-1"
//               style={{
//                 background: 'linear-gradient(90deg, #3a86ff, #ff006e)',
//                 WebkitBackgroundClip: 'text',
//                 WebkitTextFillColor: 'transparent',
//               }}
//             >
//               Hi there, John
//             </h1>
//             <h2
//               className="text-[2rem] leading-tight font-bold mb-3"
//               style={{
//                 background: 'linear-gradient(90deg, #3a86ff, #ff006e)',
//                 WebkitBackgroundClip: 'text',
//                 WebkitTextFillColor: 'transparent',
//               }}
//             >
//               What would you like to know?
//             </h2>
//             <p className="text-gray-500 text-[0.925rem]">
//               Use one of the most common prompts below or use your own to begin
//             </p>
//           </div> */}
//         <div className="mb-10">
//   <h1 className="text-[2.75rem] leading-tight font-bold mb-1" style={{
//         background: "linear-gradient(90deg, #000000, #ff006e, #9333EA)",
//         WebkitBackgroundClip: "text",
//         WebkitTextFillColor: "transparent",
//       }}>
//     Hi there,{" "}
//     <span
//       className="font-bold"
//       style={{
//         background: "linear-gradient(90deg, #000000, #ff006e, #9333EA)",
//         WebkitBackgroundClip: "text",
//         WebkitTextFillColor: "transparent",
//       }}
//     >
//       John
//     </span>
//   </h1>
//   <h2 className="text-[2rem] leading-tight font-bold mb-3" style={{
//         background: "linear-gradient(90deg, #000000, #ff006e, #9333EA)",
//         WebkitBackgroundClip: "text",
//         WebkitTextFillColor: "transparent",
//       }}>
//     What{" "}
//     <span
//       style={{
//         background: "linear-gradient(90deg, #000000, #ff006e, #9333EA)",
//         WebkitBackgroundClip: "text",
//         WebkitTextFillColor: "transparent",
//       }}
//     >
//       would
//     </span>{" "}
//     <span
//       style={{
//         background: "linear-gradient(90deg, #000000, #ff006e, #4F46E5)",
//         WebkitBackgroundClip: "text",
//         WebkitTextFillColor: "transparent",
//       }}
//     >
//       like to know?
//     </span>
//   </h2>
//   <p className="text-gray-500 text-[0.925rem]">
//     Use one of the most common prompts below or use your own to begin
//   </p>
// </div>


//           {/* Prompt Grid */}
//           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
//             {prompts.map((prompt, index) => (
//               <button
//                 key={index}
//                 className="bg-white p-5 rounded-xl border border-gray-100 hover:shadow-md transition-shadow duration-200"
//               >
//                 <div className="flex flex-col h-full min-h-[120px]">
//                   <div className="mb-3 opacity-80">
//                     {prompt.icon}
//                   </div>
//                   <p className="text-sm text-gray-700 text-left font-normal">
//                     {prompt.title}
//                   </p>
//                 </div>
//               </button>
//             ))}
//           </div>

//           {/* Refresh Prompts */}
//           <button className="flex items-center space-x-2 text-gray-500 hover:text-gray-600 text-sm mb-8">
//             <RefreshCw className="w-4 h-4" />
//             <span>Refresh Prompts</span>
//           </button>

//           {/* Input Area */}
//           <div className="relative bg-white rounded-2xl border border-gray-100 shadow-sm">
//             <textarea
//               className="w-full resize-none px-6 pt-5 pb-16 rounded-2xl focus:outline-none text-gray-700 placeholder-gray-500 text-[15px]"
//               placeholder="Ask whatever you want...."
//               value={message}
//               onChange={(e) => setMessage(e.target.value)}
//               rows={1}
//             />
//             <div className="absolute bottom-4 inset-x-6 flex justify-between items-center">
//               <div className="flex items-center space-x-4">
//                 <button className="text-gray-400 hover:text-gray-600 transition-colors">
//                   <Paperclip className="w-5 h-5" />
//                 </button>
//                 <button className="text-gray-400 hover:text-gray-600 transition-colors">
//                   <ImageIcon className="w-5 h-5" />
//                 </button>
//               </div>
//               <div className="flex items-center space-x-3">
//                 <span className="text-gray-400 text-sm">0/1000</span>
//                 <div className="relative">
//                   <select className="appearance-none bg-gray-50 text-sm text-gray-600 pl-3 pr-8 py-1.5 rounded-lg border border-gray-200 focus:outline-none">
//                     <option>All Web</option>
//                   </select>
//                   <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
//                     <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
//                     </svg>
//                   </div>
//                 </div>
//                 <button 
//                   className={`p-2 rounded-lg flex items-center justify-center ${
//                     message.trim() 
//                       ? 'bg-[#4F46E5] text-white hover:bg-[#4338CA]' 
//                       : 'bg-gray-100 text-gray-400'
//                   }`}
//                 >
//                   <Send className="w-4 h-4" />
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>

//       </main>
//     </div>
//   );
// };

// export default AgentChat;


// import React, { useState } from 'react';
// import { 
//     FaTimes as X, 
//     FaPlus as Plus, 
//     FaSearch as Search, 
//     FaHome as Home, 
//     FaFileAlt as FileText, 
//     FaClock as Clock, 
//     FaSyncAlt as RefreshCw, 
//     FaPaperclip as Paperclip, 
//     FaImage as ImageIcon, 
//     FaPaperPlane as Send, 
//     FaUser as User, 
//     FaEnvelope as Mail, 
//     FaFileAlt as Document, 
//     FaCog as Settings 
//   } from 'react-icons/fa';

// const AgentChat = () => {
//   const [message, setMessage] = useState('');
//   const prompts = [
//     {
//       title: 'Write a to-do list for a personal project or task',
//       icon: <User className="w-5 h-5 text-gray-700" />
//     },
//     {
//       title: 'Generate an email to reply to a job offer',
//       icon: <Mail className="w-5 h-5 text-gray-700" />
//     },
//     {
//       title: 'Summarise this article or text for me in one paragraph',
//       icon: <Document className="w-5 h-5 text-gray-700" />
//     },
//     {
//       title: 'How does AI work in a technical capacity',
//       icon: <Settings className="w-5 h-5 text-gray-700" />
//     }
//   ];

//   return (
//     <div className="flex h-screen bg-[#F8F9FB]">
//       {/* Sidebar */}
//       <aside className="w-[68px] bg-white border-r border-gray-200 flex flex-col items-center py-4">
//         <div className="flex flex-col items-center space-y-6">
//           <button className="p-2.5 rounded-lg hover:bg-gray-100">
//             <X className="w-6 h-6 text-gray-600" />
//           </button>
//           <button className="p-2.5 rounded-lg hover:bg-gray-100">
//             <Plus className="w-6 h-6 text-gray-600" />
//           </button>
//           <button className="p-2.5 rounded-lg hover:bg-gray-100">
//             <Search className="w-6 h-6 text-gray-600" />
//           </button>
//           <button className="p-2.5 rounded-lg hover:bg-gray-100">
//             <Home className="w-6 h-6 text-gray-600" />
//           </button>
//           <button className="p-2.5 rounded-lg hover:bg-gray-100">
//             <FileText className="w-6 h-6 text-gray-600" />
//           </button>
//           <button className="p-2.5 rounded-lg hover:bg-gray-100">
//             <Clock className="w-6 h-6 text-gray-600" />
//           </button>
//         </div>
//       </aside>

//       {/* Main Content */}
//       <main className="flex-1 px-8 py-12">
//         <div className="max-w-[1200px] mx-auto">
//           {/* Welcome Message */}
//           <div className="mb-10">
//             <h1 className="text-[2.75rem] leading-tight font-normal text-gray-900 mb-1">
//               Hi there, <span className="text-[#9333EA]">John</span>
//             </h1>
//             <h2 className="text-[2rem] leading-tight font-normal text-gray-900 mb-3">
//               What <span className="text-[#9333EA]">would</span> <span className="text-[#4F46E5]">like to know?</span>
//             </h2>
//             <p className="text-gray-500 text-[0.925rem]">
//               Use one of the most common prompts below or use your own to begin
//             </p>
//           </div>

//           {/* Prompt Grid */}
//           <div className="grid grid-cols-4 gap-4 mb-6">
//             {prompts.map((prompt, index) => (
//               <button
//                 key={index}
//                 className="bg-white p-5 rounded-xl border border-gray-100 hover:shadow-md transition-shadow duration-200"
//               >
//                 <div className="flex flex-col h-full min-h-[120px]">
//                   <div className="mb-3 opacity-80">
//                     {prompt.icon}
//                   </div>
//                   <p className="text-sm text-gray-700 text-left font-normal">
//                     {prompt.title}
//                   </p>
//                 </div>
//               </button>
//             ))}
//           </div>

//           {/* Refresh Prompts */}
//           <button className="flex items-center space-x-2 text-gray-500 hover:text-gray-600 text-sm mb-8">
//             <RefreshCw className="w-4 h-4" />
//             <span>Refresh Prompts</span>
//           </button>

//           {/* Input Area */}
//           <div className="relative bg-white rounded-2xl border border-gray-100 shadow-sm">
//             <textarea
//               className="w-full resize-none px-6 pt-5 pb-16 rounded-2xl focus:outline-none text-gray-700 placeholder-gray-500 text-[15px]"
//               placeholder="Ask whatever you want...."
//               value={message}
//               onChange={(e) => setMessage(e.target.value)}
//               rows={1}
//             />
//             <div className="absolute bottom-4 inset-x-6 flex justify-between items-center">
//               <div className="flex items-center space-x-4">
//                 <button className="text-gray-400 hover:text-gray-600 transition-colors">
//                   <Paperclip className="w-5 h-5" />
//                 </button>
//                 <button className="text-gray-400 hover:text-gray-600 transition-colors">
//                   <ImageIcon className="w-5 h-5" />
//                 </button>
//               </div>
//               <div className="flex items-center space-x-3">
//                 <span className="text-gray-400 text-sm">0/1000</span>
//                 <div className="relative">
//                   <select className="appearance-none bg-gray-50 text-sm text-gray-600 pl-3 pr-8 py-1.5 rounded-lg border border-gray-200 focus:outline-none">
//                     <option>All Web</option>
//                   </select>
//                   <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
//                     <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
//                     </svg>
//                   </div>
//                 </div>
//                 <button 
//                   className={`p-2 rounded-lg flex items-center justify-center ${
//                     message.trim() 
//                       ? 'bg-[#4F46E5] text-white hover:bg-[#4338CA]' 
//                       : 'bg-gray-100 text-gray-400'
//                   }`}
//                 >
//                   <Send className="w-4 h-4" />
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* User Avatar */}
//         <div className="fixed bottom-6 left-24">
//           <div className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-white">
//             <img 
//               src="/api/placeholder/40/40" 
//               alt="User avatar"
//               className="w-full h-full object-cover"
//             />
//           </div>
//         </div>
//       </main>
//     </div>
//   );
// };

// export default AgentChat;


// import React, { useState } from 'react';
// import { 
//     FaTimes as X, 
//     FaPlus as Plus, 
//     FaSearch as Search, 
//     FaHome as Home, 
//     FaFileAlt as FileText, 
//     FaClock as Clock, 
//     FaSyncAlt as RefreshCw, 
//     FaPaperclip as Paperclip, 
//     FaImage as ImageIcon, 
//     FaPaperPlane as Send 
//   } from 'react-icons/fa';

// const AgentChat = () => {
//   const [message, setMessage] = useState('');
//   const prompts = [
//     {
//       title: 'Write a to-do list for a personal project or task',
//       icon: <Home className="w-5 h-5 text-gray-600" />
//     },
//     {
//       title: 'Generate an email to reply to a job offer',
//       icon: <FileText className="w-5 h-5 text-gray-600" />
//     },
//     {
//       title: 'Summarise this article or text for me in one paragraph',
//       icon: <FileText className="w-5 h-5 text-gray-600" />
//     },
//     {
//       title: 'How does AI work in a technical capacity',
//       icon: <Home className="w-5 h-5 text-gray-600" />
//     }
//   ];

//   return (
//     <div className="flex h-screen bg-[#F8F9FB]">
//       {/* Sidebar */}
//       <aside className="w-[68px] bg-white border-r border-gray-200 flex flex-col items-center py-3">
//         <div className="flex flex-col items-center gap-1">
//           <button className="p-2.5 rounded-lg hover:bg-gray-100">
//             <X className="w-6 h-6 text-gray-600" />
//           </button>
//           <button className="p-2.5 rounded-lg hover:bg-gray-100">
//             <Plus className="w-6 h-6 text-gray-600" />
//           </button>
//           <button className="p-2.5 rounded-lg hover:bg-gray-100">
//             <Search className="w-6 h-6 text-gray-600" />
//           </button>
//           <button className="p-2.5 rounded-lg hover:bg-gray-100">
//             <Home className="w-6 h-6 text-gray-600" />
//           </button>
//           <button className="p-2.5 rounded-lg hover:bg-gray-100">
//             <FileText className="w-6 h-6 text-gray-600" />
//           </button>
//           <button className="p-2.5 rounded-lg hover:bg-gray-100">
//             <Clock className="w-6 h-6 text-gray-600" />
//           </button>
//         </div>
//       </aside>

//       {/* Main Content */}
//       <main className="flex-1 px-8 py-12">
//         <div className="max-w-[1200px] mx-auto">
//           {/* Welcome Message */}
//           <div className="mb-10">
//             <h1 className="text-[2.5rem] leading-tight font-medium text-gray-900 mb-2">
//               Hi there, <span className="text-[#9333EA]">John</span>
//             </h1>
//             <h2 className="text-[2rem] leading-tight font-medium text-gray-900 mb-3">
//               What <span className="text-[#9333EA]">would</span> <span className="text-[#4F46E5]">like to know?</span>
//             </h2>
//             <p className="text-gray-500 text-[0.925rem]">
//               Use one of the most common prompts below or use your own to begin
//             </p>
//           </div>

//           {/* Prompt Grid */}
//           <div className="grid grid-cols-4 gap-4 mb-6">
//             {prompts.map((prompt, index) => (
//               <button
//                 key={index}
//                 className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow duration-200 group flex items-start gap-3 min-h-[130px]"
//               >
//                 <div className="mt-1">{prompt.icon}</div>
//                 <p className="text-sm text-gray-700 text-left font-normal">
//                   {prompt.title}
//                 </p>
//               </button>
//             ))}
//           </div>

//           {/* Refresh Prompts */}
//           <button className="flex items-center gap-2 text-gray-500 text-sm mb-8 hover:text-gray-600 group">
//             <RefreshCw className="w-4 h-4 group-hover:rotate-180 transition-transform duration-300" />
//             <span>Refresh Prompts</span>
//           </button>

//           {/* Input Area */}
//           <div className="relative bg-white rounded-xl border border-gray-200 shadow-sm">
//             <textarea
//               className="w-full resize-none px-6 pt-5 pb-16 rounded-xl focus:outline-none text-gray-700 text-base"
//               placeholder="Ask whatever you want...."
//               value={message}
//               onChange={(e) => setMessage(e.target.value)}
//               rows={1}
//             />
//             <div className="absolute bottom-4 left-5 right-5 flex justify-between items-center">
//               <div className="flex gap-4">
//                 <button className="text-gray-400 hover:text-gray-600 transition-colors">
//                   <Paperclip className="w-5 h-5" />
//                 </button>
//                 <button className="text-gray-400 hover:text-gray-600 transition-colors">
//                   <ImageIcon className="w-5 h-5" />
//                 </button>
//               </div>
//               <div className="flex items-center gap-3">
//                 <span className="text-gray-400 text-sm">0/1000</span>
//                 <div className="relative">
//                   <select className="appearance-none bg-gray-50 text-sm text-gray-600 pl-3 pr-8 py-1.5 rounded-lg border border-gray-200 focus:outline-none focus:border-gray-300">
//                     <option>All Web</option>
//                   </select>
//                   <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
//                     <svg className="fill-current h-4 w-4" viewBox="0 0 20 20">
//                       <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
//                     </svg>
//                   </div>
//                 </div>
//                 <button 
//                   className={`p-2 rounded-lg transition-colors ${
//                     message.trim() 
//                       ? 'bg-[#4F46E5] text-white hover:bg-[#4338CA]' 
//                       : 'bg-gray-100 text-gray-400'
//                   }`}
//                 >
//                   <Send className="w-4 h-4" />
//                 </button>
//               </div>
//             </div>
//           </div>

//           {/* User Avatar */}
//           <div className="fixed bottom-6 left-24">
//             <div className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-white">
//               <img 
//                 src="/api/placeholder/40/40" 
//                 alt="User avatar"
//                 className="w-full h-full object-cover"
//               />
//             </div>
//           </div>
//         </div>
//       </main>
//     </div>
//   );
// };

// export default AgentChat;


// import React, { useState } from 'react';
// import { 
//     FaTimes as X, 
//     FaPlus as Plus, 
//     FaSearch as Search, 
//     FaHome as Home, 
//     FaFile as File, 
//     FaClock as Clock, 
//     FaSyncAlt as RefreshCw, 
//     FaPaperclip as Paperclip, 
//     FaImage as ImageIcon, 
//     FaPaperPlane as Send 
//   } from 'react-icons/fa';

// const AgentChat = () => {
//   const [message, setMessage] = useState('');
//   const prompts = [
//     {
//       title: 'Write a to-do list for a personal project or task',
//       icon: 'user'
//     },
//     {
//       title: 'Generate an email to reply to a job offer',
//       icon: 'mail'
//     },
//     {
//       title: 'Summarise this article or text for me in one paragraph',
//       icon: 'file'
//     },
//     {
//       title: 'How does AI work in a technical capacity',
//       icon: 'settings'
//     }
//   ];

//   return (
//     <div className="flex h-screen bg-gray-50">
//       {/* Sidebar */}
//       <div className="w-16 bg-white border-r flex flex-col items-center py-4 space-y-8">
//         <button className="p-2 rounded-lg hover:bg-gray-100">
//           <X className="w-6 h-6 text-gray-600" />
//         </button>
//         <button className="p-2 rounded-lg hover:bg-gray-100">
//           <Plus className="w-6 h-6 text-gray-600" />
//         </button>
//         <button className="p-2 rounded-lg hover:bg-gray-100">
//           <Search className="w-6 h-6 text-gray-600" />
//         </button>
//         <button className="p-2 rounded-lg hover:bg-gray-100">
//           <Home className="w-6 h-6 text-gray-600" />
//         </button>
//         <button className="p-2 rounded-lg hover:bg-gray-100">
//           <File className="w-6 h-6 text-gray-600" />
//         </button>
//         <button className="p-2 rounded-lg hover:bg-gray-100">
//           <Clock className="w-6 h-6 text-gray-600" />
//         </button>
//       </div>

//       {/* Main Content */}
//       <div className="flex-1 py-12 px-8 overflow-auto">
//         <div className="max-w-5xl mx-auto">
//           {/* Welcome Message */}
//           <div className="mb-12">
//             <h1 className="text-4xl font-medium mb-2">
//               Hi there, <span className="text-purple-600">John</span>
//             </h1>
//             <h2 className="text-3xl font-medium mb-3">
//               What <span className="text-purple-600">would</span> <span className="text-indigo-600">like to know?</span>
//             </h2>
//             <p className="text-gray-500 text-sm">
//               Use one of the most common prompts below or use your own to begin
//             </p>
//           </div>

//           {/* Prompt Grid */}
//           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
//             {prompts.map((prompt, index) => (
//               <button
//                 key={index}
//                 className="p-6 bg-white rounded-xl border border-gray-100 hover:shadow-md transition-shadow text-left min-h-[120px] flex items-center"
//               >
//                 <p className="text-sm text-gray-700">{prompt.title}</p>
//               </button>
//             ))}
//           </div>

//           {/* Refresh Prompts */}
//           <button className="flex items-center text-gray-500 text-sm mb-8 hover:text-gray-600">
//             <RefreshCw className="w-4 h-4 mr-2" />
//             <span>Refresh Prompts</span>
//           </button>

//           {/* Input Area */}
//           <div className="relative bg-white rounded-xl border border-gray-100">
//             <textarea
//               className="w-full resize-none p-4 rounded-xl focus:outline-none text-gray-700 min-h-[100px]"
//               placeholder="Ask whatever you want...."
//               value={message}
//               onChange={(e) => setMessage(e.target.value)}
//             />
//             <div className="absolute bottom-3 left-4 right-4 flex justify-between items-center">
//               <div className="flex space-x-4">
//                 <button className="text-gray-400 hover:text-gray-600">
//                   <Paperclip className="w-5 h-5" />
//                 </button>
//                 <button className="text-gray-400 hover:text-gray-600">
//                   <ImageIcon className="w-5 h-5" />
//                 </button>
//               </div>
//               <div className="flex items-center space-x-3">
//                 <div className="flex items-center space-x-2">
//                   <span className="text-gray-400 text-sm">0/1000</span>
//                   <select className="bg-gray-50 text-sm text-gray-600 py-1 px-2 rounded-md border border-gray-200">
//                     <option>All Web</option>
//                   </select>
//                 </div>
//                 <button 
//                   className={`p-2 rounded-lg ${
//                     message.trim() 
//                       ? 'bg-indigo-600 text-white hover:bg-indigo-700' 
//                       : 'bg-gray-100 text-gray-400'
//                   }`}
//                 >
//                   <Send className="w-4 h-4" />
//                 </button>
//               </div>
//             </div>
//           </div>

//           {/* User Avatar */}
//           <div className="fixed bottom-6 left-6">
//             <div className="w-10 h-10 rounded-full bg-gray-300 overflow-hidden">
//               <img 
//                 src="/api/placeholder/40/40" 
//                 alt="User avatar"
//                 className="w-full h-full object-cover"
//               />
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AgentChat;


// import React, { useState } from "react";
// import { FiSend } from "react-icons/fi";
// import { FaPaperclip, FaBars, FaChevronDown } from "react-icons/fa";

// const agents = ["Google Drive Agent", "Database Agent", "Email Agent", "Workflow Agent"];

// const ChatPage: React.FC = () => {
//     const [selectedAgent, setSelectedAgent] = useState<string>("GPT 4.0 Mini");
//     const [chatHistory, setChatHistory] = useState<string[]>([
//         "Hello! How can I assist you today?",
//         "I'm just a program, but I'm here and ready to help you! What do you need assistance with?",
//     ]);
//     const [newMessage, setNewMessage] = useState<string>("");
//     const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

//     const handleAgentChange = (agent: string) => {
//         setSelectedAgent(agent);
//         setIsDropdownOpen(false);
//     };

//     const sendMessage = () => {
//         if (newMessage.trim() === "") return;
//         setChatHistory((prev) => [...prev, `You: ${newMessage}`]);
//         setNewMessage("");
//     };

//     return (
//         <div className="h-screen flex flex-col bg-black text-white pt-20">
//             {/* Top Bar */}
//             <div className="flex items-center justify-between px-4 py-3 bg-black shadow-lg z-10">
//                 {/* Left Menu */}
//                 <div className="relative">
//                     <button className="p-2 bg-black rounded-lg hover:bg-gray-700">
//                         <FaBars />
//                     </button>
//                 </div>
//                 {/* Dropdown for Agents */}
//                 <div className="relative">
//                     <button
//                         onClick={() => setIsDropdownOpen(!isDropdownOpen)}
//                         className="flex items-center px-4 py-2 rounded-lg text-white bg-black hover:bg-gray-700"
//                     >
//                         {selectedAgent} <FaChevronDown className="ml-2" />
//                     </button>
//                     {isDropdownOpen && (
//                         <ul className="absolute mt-2 right-0 bg-gray-800 shadow-lg rounded-lg text-sm z-10 w-48">
//                             {agents.map((agent) => (
//                                 <li
//                                     key={agent}
//                                     onClick={() => handleAgentChange(agent)}
//                                     className="px-4 py-2 hover:bg-gray-600 cursor-pointer text-white"
//                                 >
//                                     {agent}
//                                 </li>
//                             ))}
//                         </ul>
//                     )}
//                 </div>
//             </div>

//             {/* Chat History Section */}
//             <div className="flex-1 overflow-y-auto p-6 pt-20">
//                 {chatHistory.map((message, index) => (
//                     <div
//                         key={index}
//                         className={`mb-4 ${message.startsWith("You:") ? "text-right" : "text-left"
//                             }`}
//                     >
//                         <div
//                             className={`inline-block p-4 rounded-lg ${message.startsWith("You:")
//                                     ? "bg-white text-black"
//                                     : "bg-black text-white"
//                                 }`}
//                         >
//                             {message}
//                         </div>
//                     </div>
//                 ))}
//             </div>

//             {/* Input Section */}
//             <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 w-11/12 md:w-3/4 lg:w-2/3 bg-black px-6 py-4 rounded-full shadow-lg">
//                 <div className="flex items-center space-x-4">
//                     {/* Attachment Icon */}
//                     <button className="p-3 text-gray-400 hover:text-gray-300 transition-colors">
//                         <FaPaperclip size={24} />
//                     </button>

//                     {/* Input Field */}
//                     <input
//                         type="text"
//                         value={newMessage}
//                         onChange={(e) => setNewMessage(e.target.value)}
//                         onKeyDown={(e) => {
//                             if (e.key === "Enter") sendMessage();
//                         }}
//                         placeholder="Type your message here..."
//                         className="flex-1 bg-white text-black text-lg px-6 py-3 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     />

//                     {/* Send Button */}
//                     <button
//                         onClick={sendMessage}
//                         className="flex items-center bg-blue-600 text-white px-6 py-3 rounded-full hover:bg-blue-700 transition-transform transform hover:scale-105"
//                     >
//                         <FiSend size={20} className="mr-2" />
//                         Send
//                     </button>
//                 </div>
//             </div>
//         </div>
//     );
// };

// export default ChatPage;


// import React, { useState } from "react";
// import { FiSend } from "react-icons/fi";
// import { FaPaperclip, FaBars, FaChevronDown } from "react-icons/fa";

// const agents = ["Google Drive Agent", "Database Agent", "Email Agent", "Workflow Agent"];

// const ChatPage: React.FC = () => {
//   const [selectedAgent, setSelectedAgent] = useState<string>("GPT 4.0 Mini");
//   const [chatHistory, setChatHistory] = useState<string[]>([
//     "Hello! How can I assist you today?",
//     "I'm just a program, but I'm here and ready to help you! What do you need assistance with?",
//   ]);
//   const [newMessage, setNewMessage] = useState<string>("");
//   const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

//   const handleAgentChange = (agent: string) => {
//     setSelectedAgent(agent);
//     setIsDropdownOpen(false); 
//   };

//   const sendMessage = () => {
//     if (newMessage.trim() === "") return;
//     setChatHistory((prev) => [...prev, `You: ${newMessage}`]);
//     setNewMessage("");
//   };

//   return (
//     <div className="h-screen flex flex-col bg-black text-white pt-5">
//       {/* Top Bar */}
//       <div className="flex items-center justify-between px-6 py-3 bg-black">
//         {/* Left Menu */}
//         <div className="relative">
//           <button className="p-2 bg-black rounded-lg hover:bg-gray-800">
//             <FaBars />
//           </button>
//           <div className="relative inline-block">
//             <button
//               onClick={() => setIsDropdownOpen(!isDropdownOpen)}
//               className="flex items-center bg-black px-4 py-2 rounded-lg text-white ml-3"
//             >
//               {selectedAgent} <FaChevronDown className="ml-2" />
//             </button>
//             {isDropdownOpen && (
//               <ul className="absolute mt-2 bg-gray-800 shadow-lg rounded-lg text-sm z-10 w-48">
//                 {agents.map((agent) => (
//                   <li
//                     key={agent}
//                     onClick={() => handleAgentChange(agent)}
//                     className="px-4 py-2 hover:bg-gray-600 cursor-pointer"
//                   >
//                     {agent}
//                   </li>
//                 ))}
//               </ul>
//             )}
//           </div>
//         </div>
//       </div>

//       <div className="flex-1 overflow-y-auto p-6 pt-20"> 
//         {chatHistory.map((message, index) => (
//           <div
//             key={index}
//             className={`mb-4 ${
//               message.startsWith("You:") ? "text-right" : "text-left"
//             }`}
//           >
//             <div
//               className={`inline-block p-4 rounded-lg ${
//                 message.startsWith("You:")
//                   ? "bg-black text-white"
//                   : "bg-white text-black"
//               }`}
//             >
//               {message}
//             </div>
//           </div>
//         ))}
//       </div>

//       <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 w-4/5 md:w-1/2 bg-white rounded-full flex items-center px-4 py-2 shadow-lg">
//         <button className="p-3 text-gray-500 hover:text-gray-300">
//           <FaPaperclip size={18} />
//         </button>
//         <input
//           type="text"
//           value={newMessage}
//           onChange={(e) => setNewMessage(e.target.value)}
//           placeholder="Send a message..."
//           className="flex-1 bg-transparent text-black px-4 py-2 focus:outline-none"
//         />
//         <button
//           onClick={sendMessage}
//           className="bg-white px-4 py-2 rounded-full text-black flex items-center"
//         >
//           <FiSend />
//         </button>
//       </div>
//     </div>
//   );
// };

// export default ChatPage;

